import { ReportRecord } from '../../../../../models/report.model';
import {
  concatValuesBy,
  defaultTxt,
  formatNumber,
  formatUomCurrency,
  getPrimarySecureImage,
  getPropertyTypeLabel,
  isDash,
  isMasked,
  isMediaImage,
  simpleValueValidation,
  UserSettings,
} from '@miq/utils';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import {
  DisplayFnArgumentsInfo,
  DisplayFunctionOptions,
  LovValues,
  VisibilitySettings,
} from '../../../../../models/template.model';
import { map } from 'rxjs/operators';
import { currencyLov, PERCENT_SIGN, PropertyType, valueWithUomPrefix } from '../us.common';
import { digger } from '@cbre/digger';
import { getMediaInformation } from '../../../templates-formatter.const';

export const getFirstElementOrGiven = data => (Array.isArray(data) ? data[0] : data);
const LESSE_ROL_POSIBLE_VALUES = ['Lessee Representative', 'Tenant Representative'];
const LESSOR_REP_ROLE_POSSIBLE_VALUES = ['Listing Representative', 'Lessor Representative'];
const SUB_LESSOR_REP_ROLE_POSSIBLE_VALUES = ['Listing Representative', 'Sub lease Representative'];
const SUBLESSOR_ID = 188;
const LESSOR_ID = 622;
const LESSOR_REP_ID = 623;
const SUBLESSOR_REP_ID = 187;
const LESSOR_FIELDS = {
  622: {
    id: 622,
    label: 'Lessor',
    field: 'company_contact_role_addresses/party_organization_display_name',
    path: "company_contact_role_addresses[role_desc='Lessor' ].party_organization_display_name",
    dataType: 'text',
  },
  623: {
    id: 623,
    label: 'Lessor Rep',
    field: 'company_contact_role_addresses/party_organization_display_name',
    path: "company_contact_role_addresses[role_desc='Listing Representative'  or role_desc='Lessor Representative' ].party_organization_display_name",
    dataType: 'text',
  },
};
const SUBLESSOR_FIELDS = {
  188: {
    id: 188,
    label: 'Sublessor',
    entity: 'lease comp',
    field: 'company_contact_role_addresses/contact_display_name',
    path: "company_contact_role_addresses[role_desc='Sub Lessor' ].party_organization_display_name",
    dataType: 'text',
  },
  187: {
    id: 187,
    label: 'Sublease Rep',
    field: 'company_contact_role_addresses/contact_display_name',
    path: "company_contact_role_addresses[role_desc='Listing Representative'  or role_desc='Sub lease Representative' ].party_organization_display_name",
    dataType: 'text',
  },
};
interface ResolvedLessorObj {
  id: number;
  label: string;
  field: string;
  path: string;
  entity?: string;
  value: any;
  isVisible: boolean;
}

export const getFormattedAddress = (
  record: ReportRecord,
  _,
  address: DisplayFnArgumentsInfo,
  city: DisplayFnArgumentsInfo,
  state: DisplayFnArgumentsInfo,
  postal: DisplayFnArgumentsInfo
) =>
  `${concatValuesBy(
    ', ',
    mapAndFormatDynamicValue(record, address),
    mapAndFormatDynamicValue(record, city),
    mapAndFormatDynamicValue(record, state)
  )} ${mapAndFormatDynamicValue(record, postal)}`;

export const getpPropertySize = (record: ReportRecord, _, PropertySize: DisplayFnArgumentsInfo) => {
  const [size] = mapAndFormatDynamicValue(record, PropertySize);
  // SF is hardcode because there is no UOM for this field
  return size !== defaultTxt ? `${formatNumber(size)} SF` : defaultTxt;
};

export const getPropertySubType = (
  record: ReportRecord,
  _,
  propertyType: DisplayFnArgumentsInfo,
  subType: DisplayFnArgumentsInfo
) => {
  const propertyTypeValue = mapAndFormatDynamicValue(record, propertyType);
  const propertyTypeLabel = getPropertyTypeLabel(propertyTypeValue as string);
  const values = concatValuesBy(' / ', propertyTypeLabel, mapAndFormatDynamicValue(record, subType));
  return values;
};

export const getBuiltReno = (record: ReportRecord, _, reno: DisplayFnArgumentsInfo, built: DisplayFnArgumentsInfo) => {
  const builtVal = mapAndFormatDynamicValue(record, built);
  const renoVal = mapAndFormatDynamicValue(record, reno);
  return `${builtVal[0] || defaultTxt} | ${renoVal[0] || defaultTxt}`;
};

export const getLocation = (
  record: ReportRecord,
  _,
  market: DisplayFnArgumentsInfo,
  submarket: DisplayFnArgumentsInfo,
  district: DisplayFnArgumentsInfo
) => {
  const marketVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, market, null, null, null, ''));
  const submarketVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, submarket, null, null, null, ''));
  const districtVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, district, null, null, null, ''));

  const locations = [marketVal, submarketVal, districtVal].filter(Boolean);
  return locations.join(' - ');
};

export const getClass = (record: ReportRecord, _, classArg: DisplayFnArgumentsInfo) => {
  return classArg.lovValues.pipe(
    map((lovs: LovValues[]) => {
      const lovKeyValue = mapAndFormatDynamicValue(record, classArg, lovs);
      const lovValue = lovs.find(a => a.key === lovKeyValue);
      return lovValue ? lovValue.value : defaultTxt;
    })
  );
};

export const getUOMValAcronym = (uomVal: string) => {
  let uomValList: string | string[] = uomVal.split(' ');
  uomValList = uomValList.reduce((acc: string, val: string) => `${acc}${val.slice(0, 1)}`, '');
  return uomValList;
};

export const getTIA = (record: ReportRecord, options, TIA: DisplayFnArgumentsInfo, TIS: DisplayFnArgumentsInfo) => {
  return currencyLov(options).pipe(
    map((currencyLOV: LovValues[]) => {
      const uomVal = mapAndFormatDynamicValue(record, TIA.uomArgument);
      if (isMasked(uomVal)) {
        return uomVal;
      }
      let TIAValue =
        uomVal && uomVal !== defaultTxt
          ? valueWithUomPrefix(record, PropertyType.Office, TIA, currencyLOV)
          : mapAndFormatDynamicValue(record, TIA);
      TIAValue = getFirstElementOrGiven(TIAValue);
      const TISValue = mapAndFormatDynamicValue(record, TIS);
      // Possible values for TISValue: [-], "No", "Yes"
      return TISValue && TISValue === 'Yes' ? `${TIAValue} / As-is` : TIAValue;
    })
  );
};

export const getAskingRent = (
  record: ReportRecord,
  options: DisplayFunctionOptions,
  askingRentYearly: DisplayFnArgumentsInfo,
  rateType: DisplayFnArgumentsInfo
) => {
  // ! HOTFIX: Update with correct displayFn parameter later on...
  const askingRentMonthly = {
    ...askingRentYearly,
    id: 1617,
    field: 'asking_rent_monthly',
    uomArgument: {
      ...askingRentYearly.uomArgument,
      id: 1618,
      field: 'ref_asking_rent_monthly_uom_desc',
    },
    get uomId() {
      return this.uomArgument.id;
    },
  };
  return getRentByMonthOrYear(record, options, askingRentMonthly as DisplayFnArgumentsInfo, rateType, askingRentYearly);
};

export const getEffectiveRent = (
  record: ReportRecord,
  options: DisplayFunctionOptions,
  effectiveRentMonthly: DisplayFnArgumentsInfo,
  effectiveRentYearly: DisplayFnArgumentsInfo,
  rateType: DisplayFnArgumentsInfo
) => {
  return getRentByMonthOrYear(record, options, effectiveRentMonthly, rateType, effectiveRentYearly);
};

export const getBaseRent = (
  record: ReportRecord,
  options: DisplayFunctionOptions,
  baseRentMonthly: DisplayFnArgumentsInfo,
  baseRentYearly: DisplayFnArgumentsInfo,
  rateType: DisplayFnArgumentsInfo
) => {
  return getRentByMonthOrYear(record, options, baseRentMonthly, rateType, baseRentYearly);
};

const getRentByMonthOrYear = (
  record: ReportRecord,
  options: DisplayFunctionOptions,
  rentMonthly: DisplayFnArgumentsInfo,
  rateType: DisplayFnArgumentsInfo,
  rentYearly: DisplayFnArgumentsInfo
) => {
  return currencyLov(options).pipe(
    map((currencyLOV: LovValues[]) => {
      const rentConfig = UserSettings.isYearlyPreference() ? rentYearly : rentMonthly;
      const uomVal = mapAndFormatDynamicValue(record, rentConfig.uomArgument);
      if (isMasked(uomVal)) {
        return uomVal;
      }
      let rentVal =
        uomVal && uomVal !== defaultTxt
          ? valueWithUomPrefix(record, PropertyType.Office, rentConfig, currencyLOV)
          : mapAndFormatDynamicValue(record, rentConfig);
      rentVal = typeof rentVal === 'string' ? rentVal : rentVal[0];

      let rateTypeVal: string | string[] = mapAndFormatDynamicValue(record, rateType) as string;
      rateTypeVal = typeof rateTypeVal === 'string' ? rateTypeVal : rateTypeVal[0];

      if (rateTypeVal && rateTypeVal !== defaultTxt) {
        rateTypeVal = getUOMValAcronym(rateTypeVal);
      }

      const suffixByTimePreference = UserSettings.isYearlyPreference() ? 'Yr' : 'Mo';

      return (rentVal && rentVal !== defaultTxt) || (rateTypeVal && rateTypeVal !== defaultTxt)
        ? `${rentVal} ${rateTypeVal.toUpperCase()} / ${suffixByTimePreference}`
        : defaultTxt;
    })
  );
};

export const getOPEX = (record: ReportRecord, options: DisplayFunctionOptions, opex: DisplayFnArgumentsInfo) => {
  return currencyLov(options).pipe(
    map((currencyLOV: LovValues[]) => {
      const uomVal = mapAndFormatDynamicValue(record, opex.uomArgument);
      let opexVal =
        uomVal && uomVal !== defaultTxt
          ? valueWithUomPrefix(record, PropertyType.Office, opex, currencyLOV)
          : mapAndFormatDynamicValue(record, opex);
      opexVal = getFirstElementOrGiven(opexVal);
      return opexVal && opexVal !== defaultTxt ? `${opexVal}/ SF` : defaultTxt;
    })
  );
};

export const getRentSteps = (
  record: ReportRecord,
  options: DisplayFunctionOptions,
  escalationOrder: DisplayFnArgumentsInfo,
  escalationAmount: DisplayFnArgumentsInfo,
  escalationPercent: DisplayFnArgumentsInfo,
  escalationPeriod: DisplayFnArgumentsInfo
) => {
  return currencyLov(options).pipe(
    map((currencyLOV: LovValues[]) => {
      const escalationPeriodValues = mapAndFormatDynamicValue(record, escalationPeriod) as string[];
      const escalationAmountValues = valueWithUomPrefix(
        record,
        PropertyType.Office,
        escalationAmount,
        currencyLOV
      ) as string[];
      const escalationPercentValues = mapAndFormatDynamicValue(record, escalationPercent) as string[];
      const escalationOrderValues = mapAndFormatDynamicValue(record, escalationOrder) as any[] as number[];
      const escalationData: {
        escalationPeriod: string;
        escalationAmount: string;
        escalationPercent: string;
        escalationOrder: number;
      }[] = escalationOrderValues.map((escalationOrderVal, index) => {
        return {
          escalationPeriod: escalationPeriodValues[index],
          escalationAmount: escalationAmountValues[index],
          escalationPercent: escalationPercentValues[index],
          escalationOrder: escalationOrderVal,
        };
      });
      const escalationValues = escalationData
        .sort((leftValue, rightValue) => {
          return leftValue.escalationOrder - rightValue.escalationOrder;
        })
        .map(escalationValue => {
          const divider = !!escalationValue.escalationAmount && !!escalationValue.escalationPercent ? ' | ' : '';
          const suffix = !!escalationValue.escalationPercent ? '%' : '';

          return !!escalationValue.escalationPeriod &&
            (!!escalationValue.escalationAmount || !!escalationValue.escalationPercent)
            ? // tslint:disable-next-line:max-line-length
              `${escalationValue.escalationPeriod}: ${escalationValue.escalationAmount}${divider}${escalationValue.escalationPercent}${suffix}`
            : null;
        });
      const values = concatValuesBy(' , ', ...escalationValues);
      return values;
    })
  );
};

export const SHORT_LIST1 = 'shortList1';
export const SHORT_LIST2 = 'shortList2';
export const LONG_LIST = 'longList';

export const isFieldVisible = (record: ReportRecord, subTemplateLists: string[]): boolean => {
  return subTemplateLists.includes(digger(record, 'data.miqProperties.subTemplate'));
};

export const getAreaLeased = (record: ReportRecord, _, areaLeased) => {
  let areaLeasedVal = mapAndFormatDynamicValue(record, areaLeased);
  areaLeasedVal = getFirstElementOrGiven(areaLeasedVal);
  return areaLeasedVal ? areaLeasedVal : defaultTxt;
};

export const getLeaseTerm = (record: ReportRecord, _, leaseTerm) => {
  let leaseTermVal = mapAndFormatDynamicValue(record, leaseTerm);
  leaseTermVal = getFirstElementOrGiven(leaseTermVal);
  return leaseTermVal ? leaseTermVal : defaultTxt;
};

export const getFreeRentMonths = (record: ReportRecord, _, freeRent) => {
  const freeRentVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, freeRent));
  if (isMasked(freeRentVal)) {
    return freeRentVal;
  }
  // Months is hardcode because there is no UOM for this field
  return freeRentVal && freeRentVal !== defaultTxt ? `${freeRentVal} Months` : defaultTxt;
};

// TODO(Aurelio): to clean up deprecated code and move things around.
enum LessorColumnsEnum {
  Lessor = 622,
  LessorRep = 623,
}

enum SubLessorColumnsEnum {
  SubLessor = 188,
  SubLessorRep = 187,
}

export function isLessorVisible(record: ReportRecord): boolean {
  // get sublease flag
  const isRecordSubLease = digger(record.data, 'sublease_f');

  // get & validate sublessor data
  const sublessorData = getSubLessorData(record, null);
  const hasSubLessor = simpleValueValidation(sublessorData) && !isDash(sublessorData);

  // get & validate sublessor rep data
  const sublessorRepData = getSubLessorRepData(record, null, null);
  const hasSubLessorRep = simpleValueValidation(sublessorRepData) && !isDash(sublessorRepData);

  const hasSubLessorOrRep = hasSubLessor || hasSubLessorRep;
  /*
   * for a lessor to be visible, the record should not be a sublease
   * (null value is considered as not a sublease, therefore the hard validation)
   * OR, it should be a sublease, but no sublessor or sublessor rep
   * should be available
   */
  return !isRecordSubLease || (!!isRecordSubLease && !hasSubLessorOrRep);
}

export function getLessorData(record: ReportRecord, _options: DisplayFunctionOptions) {
  const lessorColumn = LESSOR_FIELDS[LessorColumnsEnum.Lessor];
  return getTenantOrLesseFormatedData(record, _options, lessorColumn as DisplayFnArgumentsInfo);
}

export function getSubLessorData(record: ReportRecord, _options: DisplayFunctionOptions) {
  const lessorColumn = SUBLESSOR_FIELDS[SubLessorColumnsEnum.SubLessor];
  return getTenantOrLesseFormatedData(record, _options, lessorColumn as DisplayFnArgumentsInfo);
}

export function getLessorRepData(
  record: ReportRecord,
  _options: DisplayFunctionOptions,
  _fieldInfo: DisplayFnArgumentsInfo
) {
  return getCompanyAndContacts(record, LESSOR_REP_ROLE_POSSIBLE_VALUES, _options, _fieldInfo);
}

export function getSubLessorRepData(
  record: ReportRecord,
  _options: DisplayFunctionOptions,
  _fieldInfo: DisplayFnArgumentsInfo
) {
  return getCompanyAndContacts(record, SUB_LESSOR_REP_ROLE_POSSIBLE_VALUES, _options, _fieldInfo);
}

/** @deprecated */
export const getLeaseFieldVisibility = (record: ReportRecord, processedFieldId: number): ResolvedLessorObj => {
  const isSubleaseField = Object.keys(SUBLESSOR_FIELDS).some(id => id === String(processedFieldId));
  const resolvedLessorFields: Array<ResolvedLessorObj> = resolveLessorInfo(
    LESSOR_FIELDS,
    isSubleaseField ? LESSOR_ID : processedFieldId,
    record
  );
  const resolvedSubLessorFields: Array<ResolvedLessorObj> = resolveLessorInfo(
    SUBLESSOR_FIELDS,
    isSubleaseField ? processedFieldId : SUBLESSOR_ID,
    record
  );
  const isRecordSublese = digger(record.data, 'sublease_f');

  resolvedLessorFields.forEach(lessorResolvedField => (lessorResolvedField.isVisible = true));
  if (!isRecordSublese) {
    resolvedSubLessorFields.forEach(subLessorRessolvedField => (subLessorRessolvedField.isVisible = false));
    return findResolvedLessor(isSubleaseField ? resolvedSubLessorFields : resolvedLessorFields, processedFieldId);
  } else {
    let areSublessorFieldsVisible: boolean;
    const resolvedSublessorField = resolvedSubLessorFields.reduce(
      (prev, act) => {
        const idCompare = isSubleaseField ? processedFieldId : SUBLESSOR_ID;
        const retVal = prev.id === idCompare ? prev : act;
        const isVisible = prev.isVisible || act.isVisible;
        retVal.isVisible = isVisible;
        areSublessorFieldsVisible = isVisible;
        return retVal;
      },
      resolvedSubLessorFields[0] || ({} as ResolvedLessorObj)
    );
    if (areSublessorFieldsVisible) {
      if (isSubleaseField) {
        return resolvedSublessorField;
      }
      resolvedLessorFields.forEach(lessorResolvedField => (lessorResolvedField.isVisible = false));
      return findResolvedLessor(resolvedLessorFields, processedFieldId);
    }
    return findResolvedLessor(isSubleaseField ? resolvedSubLessorFields : resolvedLessorFields, processedFieldId);
  }
};

function resolveLessorInfo(lessorFields: any, processedField: number, record: ReportRecord): Array<ResolvedLessorObj> {
  const nonProcessedField = Object.keys(lessorFields).find(id => id != String(processedField));
  const resolvedProcessedField = resolveField(lessorFields[processedField], record);
  const resolvedNonPocessedField = resolveField(lessorFields[nonProcessedField], record);
  return [resolvedProcessedField, resolvedNonPocessedField];
}

function resolveField(lessorField: any, record: ReportRecord): ResolvedLessorObj {
  let fieldVal = digger(record.data, lessorField.path);
  if (Array.isArray(fieldVal)) {
    fieldVal = [...new Set(fieldVal.filter(val => !!val))];
    if (fieldVal.length > 1) {
      fieldVal = fieldVal.join(', ');
    }
  }
  const isFieldVisible = simpleValueValidation(fieldVal);
  const fieldValue = isFieldVisible ? fieldVal : '-';
  return { ...lessorField, value: fieldValue, isVisible: isFieldVisible };
}

function findResolvedLessor(lessorArr: Array<ResolvedLessorObj>, resolvedFieldID: number): ResolvedLessorObj {
  return lessorArr.find(lessorResolvedField => lessorResolvedField.id === resolvedFieldID);
}

/** @deprecated */
export const getLessor = (
  record: ReportRecord,
  _options: DisplayFunctionOptions,
  _tenantOrLesse: DisplayFnArgumentsInfo
) => {
  const lessorResolvedField = getLeaseFieldVisibility(record, LESSOR_ID);
  return lessorResolvedField.value;
};

/** @deprecated */
export const getLessorVisibility = (record: ReportRecord, settings: VisibilitySettings) => {
  const lessorFieldId = settings.field.id;
  const lessorResolvedField = getLeaseFieldVisibility(record, lessorFieldId);
  return lessorResolvedField.isVisible;
};

/** @deprecated */
export const getLessorRep = (
  record: ReportRecord,
  _options: DisplayFunctionOptions,
  _tenantOrLesse: DisplayFnArgumentsInfo
) => {
  const lessorRepResolvedField = getLeaseFieldVisibility(record, LESSOR_REP_ID);
  return lessorRepResolvedField.value;
};

/** @deprecated */
export const getLessorRepVisibility = (record: ReportRecord, settings: VisibilitySettings) => {
  if (settings?.reportOptions?.brokerAgency) {
    return !settings?.reportOptions?.brokerAgency;
  }
  const lessorFieldId = settings.field.id;
  const lessorRepResolvedField = getLeaseFieldVisibility(record, lessorFieldId);
  return lessorRepResolvedField.isVisible;
};

/** @deprecated */
export const getSubLessor = (
  record: ReportRecord,
  _options: DisplayFunctionOptions,
  _fieldInfo: DisplayFnArgumentsInfo
) => {
  const subLessorResolvedField = getLeaseFieldVisibility(record, SUBLESSOR_ID);
  return subLessorResolvedField.value;
};

/** @deprecated */
export const getSublessorVisibility = (record: ReportRecord, settings: VisibilitySettings) => {
  const sublessorFieldId = settings.field.id;
  const subLessorResolvedField = getLeaseFieldVisibility(record, sublessorFieldId);
  return subLessorResolvedField.isVisible;
};

/** @deprecated */
export const getSubLessorRep = (
  record: ReportRecord,
  _options: DisplayFunctionOptions,
  _fieldInfo: DisplayFnArgumentsInfo
) => {
  const subLessorRepResolvedField = getLeaseFieldVisibility(record, SUBLESSOR_REP_ID);
  return subLessorRepResolvedField.value;
};

/** @deprecated */
export const getSublessorRepVisibility = (record: ReportRecord, settings: VisibilitySettings) => {
  if (settings?.reportOptions?.brokerAgency) {
    return !settings?.reportOptions?.brokerAgency;
  }
  const sublessorRepfieldId = settings.field.id;
  const subLessorRepResolvedField = getLeaseFieldVisibility(record, sublessorRepfieldId);
  return subLessorRepResolvedField.isVisible;
};

export const getEscalations = (record: ReportRecord, _, annualEscalationPercent) => {
  let escalationText = defaultTxt;
  const annualEscalationPercentValue = getFirstElementOrGiven(
    mapAndFormatDynamicValue(record, annualEscalationPercent, null, null, null, null)
  );
  const data = record.data;
  if (!!annualEscalationPercentValue) {
    escalationText =
      annualEscalationPercentValue && annualEscalationPercentValue !== defaultTxt
        ? `${annualEscalationPercentValue}${PERCENT_SIGN}`
        : defaultTxt;
  } else if (!!data.lease_escalation_value) {
    escalationText = formatUomCurrency(
      {
        valuePath: 'lease_escalation_value',
        uomPath: 'ref_lease_escalation_value_uom_desc',
      },
      data
    );
  } else if (!!data.rent_escalation_comments) {
    escalationText = data.rent_escalation_comments;
  }
  return escalationText;
};

export const getSuites = (record: ReportRecord, _: DisplayFunctionOptions, suite: DisplayFnArgumentsInfo) => {
  const suiteVal = mapAndFormatDynamicValue(record, suite);
  return Array.isArray(suiteVal) ? suiteVal.join(', ') : suiteVal || defaultTxt;
};

const getTenantOrLesseFormatedData = (
  record: ReportRecord,
  options: DisplayFunctionOptions,
  tenantOrLesse: DisplayFnArgumentsInfo
) => {
  let tenantOrLesseVal = displayFnWrapper(record, options, tenantOrLesse) || [];
  // Could be an array with more than one value
  const tenantsOrLesseVals = Array.isArray(tenantOrLesseVal)
    ? Array.from(new Set(tenantOrLesseVal)).join(', ')
    : tenantOrLesseVal;
  return tenantsOrLesseVals ? tenantsOrLesseVals : defaultTxt;
};

export const getTenat = getTenantOrLesseFormatedData;

/**
 * Use the displayFnWrapper to get the value but it is an array of one item,
 * so this function validate the type an returns the first value as string or the default text
 * @param record ReportRecord
 * @param options DisplayFunctionOptions
 * @param fieldInfo DisplayFnArgumentsInfo
 * @returns string
 */
export const getFormatedUnicValueInArrayAsString = (
  record: ReportRecord,
  options: DisplayFunctionOptions,
  fieldInfo: DisplayFnArgumentsInfo
) => {
  let farmattedVal = displayFnWrapper(record, options, fieldInfo);
  farmattedVal = getFirstElementOrGiven(farmattedVal);
  return farmattedVal ? farmattedVal : defaultTxt;
};

export const getCompanyAndContacts = (
  record: ReportRecord,
  rolTofilter: string[],
  _options: DisplayFunctionOptions,
  _fieldInfo: DisplayFnArgumentsInfo
) => {
  // Get the list of companies filtered by rol
  let companyRepList = digger(record.data, 'company_contact_role_addresses') || [];
  companyRepList = companyRepList.filter(comp => rolTofilter.includes(comp.role_desc));

  // Get company ids without repeated values
  const companyIds = [...new Set(companyRepList.map(comp => comp.company_id))];
  const formattedCompanyAndContantcs = [];

  companyIds.forEach(companyId => {
    const companyName =
      (companyRepList.find(comp => comp.company_id === companyId) || {}).party_organization_display_name || defaultTxt;
    const contactsList = companyRepList.filter(
      comp => comp.contact_id && comp.contact_display_name && comp.company_id === companyId
    );
    const contacts = contactsList.map(contact => contact.contact_display_name);
    const formattedContacts = contacts.length ? ` - ${contacts.join(', ')}` : '';
    // Company 1 - Contact 1, Contact 2, Contact 3 | Company 2  | Company 3 - Contact 1, Contact 2
    formattedCompanyAndContantcs.push(`${companyName}${formattedContacts}`);
  });

  return formattedCompanyAndContantcs.length ? formattedCompanyAndContantcs.join(' | ') : defaultTxt;
};

export const getLeaseImage = (record: ReportRecord, options: DisplayFunctionOptions) => {
  const { urlPrefix, urlSplitter } = options;
  // Get media images
  const leaseMediaImages = (record.data.getMediaInformation() || []).filter(media => isMediaImage(media));
  const propertyMediaImages = (getMediaInformation(record) || []).filter(media => isMediaImage(media));

  // Get primary image or firts image of the lease or property
  const settings = { urlPrefix, urlSplitter, onlyUrl: true };
  return getPrimarySecureImage(leaseMediaImages, propertyMediaImages, settings) as string;
};

export const getTenantRep = (
  record: ReportRecord,
  _options: DisplayFunctionOptions,
  _fieldInfo: DisplayFnArgumentsInfo
) => getCompanyAndContacts(record, LESSE_ROL_POSIBLE_VALUES, _options, _fieldInfo);
